import type { LatLon } from "@radar-sim/schema";
import type { AreaData, FerryTemplate, Mark } from "./types.js";
import { MORAY_FIRTH_COASTLINE } from "./moray-firth-coastline.js";
import type { TidalAnomaly } from "./weymouth-portland.js";

/**
 * Moray Firth — Inverness round to Buckie. North-east Scotland teaching
 * coverage. Coastline is OSM-derived (see moray-firth-coastline.ts);
 * marks, lanes, ferry templates, and tidal anomalies are hand-curated
 * placeholders sized for teaching scenarios — they're plausible but
 * not survey-grade, in line with the rest of the area data.
 */

/**
 * Moray Firth tidal anomalies. The big ones are the Sutors of
 * Cromarty (acceleration through the Cromarty Firth narrows), the
 * Chanonry Strait between Black Isle and Fortrose (where the Beauly
 * stream squeezes through), and the harbour-bar overfalls at
 * Lossiemouth and Buckie. Surfaced via getTidalAnomalies("moray-firth").
 */
export const MORAY_FIRTH_TIDAL_ANOMALIES: ReadonlyArray<TidalAnomaly> = [
  {
    id: "sutors-of-cromarty",
    name: "Sutors of Cromarty",
    severity: 0.65,
    // Narrows at the Cromarty Firth entrance — strong flood and ebb
    // streams through the gap between South and North Sutor.
    polygon: [
      { lat: 57.685, lon: -4.05 },
      { lat: 57.695, lon: -4.04 },
      { lat: 57.69, lon: -4.0 },
      { lat: 57.675, lon: -3.99 },
      { lat: 57.665, lon: -4.02 },
    ],
    // Flood goes west into the firth.
    drift: { bearingDeg: 270, speedKnots: 2.5 },
  },
  {
    id: "chanonry-narrows",
    name: "Chanonry Narrows",
    severity: 0.55,
    // Strait between Chanonry Point (Black Isle) and Fort George —
    // the Beauly Firth tidal stream funnels through here.
    polygon: [
      { lat: 57.583, lon: -4.105 },
      { lat: 57.59, lon: -4.085 },
      { lat: 57.575, lon: -4.07 },
      { lat: 57.567, lon: -4.09 },
    ],
    // Flood is north-east.
    drift: { bearingDeg: 50, speedKnots: 2 },
  },
  {
    id: "lossiemouth-bar",
    name: "Lossiemouth bar overfalls",
    severity: 0.4,
    // Tide rips across the harbour-mouth bar, awkward in onshore F4+.
    polygon: [
      { lat: 57.722, lon: -3.28 },
      { lat: 57.725, lon: -3.265 },
      { lat: 57.717, lon: -3.262 },
      { lat: 57.715, lon: -3.278 },
    ],
    // Onshore set on a flood.
    drift: { bearingDeg: 180, speedKnots: 1.5 },
  },
  {
    id: "tarbat-ness-race",
    name: "Tarbat Ness race",
    severity: 0.55,
    // North-east edge of the firth — a tidal race off the headland on
    // both flood and ebb.
    polygon: [
      { lat: 57.84, lon: -3.85 },
      { lat: 57.85, lon: -3.83 },
      { lat: 57.84, lon: -3.81 },
      { lat: 57.83, lon: -3.83 },
    ],
    drift: { bearingDeg: 110, speedKnots: 2 },
  },
];

const MARKS: ReadonlyArray<Mark> = [
  {
    id: "chanonry-point",
    name: "Chanonry Point lighthouse",
    position: { lat: 57.573, lon: -4.097 },
    type: "light",
    rcs: 0.55,
    light: {
      character: "Iso.W.6s",
      pattern: { kind: "iso", periodSec: 6 },
      color: "W",
      heightM: 12,
      rangeNm: 14,
    },
  },
  {
    id: "fort-george",
    name: "Fort George",
    position: { lat: 57.585, lon: -4.073 },
    type: "tower",
    rcs: 0.6,
  },
  {
    id: "covesea-skerries",
    name: "Covesea Skerries lighthouse",
    position: { lat: 57.715, lon: -3.339 },
    type: "light",
    rcs: 0.55,
    light: {
      character: "Fl(2).W.20s",
      pattern: { kind: "flash", periodSec: 20 },
      color: "W",
      heightM: 49,
      rangeNm: 24,
    },
  },
  {
    id: "tarbat-ness-light",
    name: "Tarbat Ness lighthouse",
    position: { lat: 57.864, lon: -3.838 },
    type: "light",
    rcs: 0.55,
    light: {
      character: "Fl(4).W.30s",
      pattern: { kind: "flash", periodSec: 30 },
      color: "W",
      heightM: 53,
      rangeNm: 24,
    },
  },
  {
    id: "inverness-firth-fairway",
    name: "Inverness Firth approach (fairway)",
    position: { lat: 57.55, lon: -4.05 },
    type: "buoy",
    rcs: 0.07,
    category: "safe-water",
  },
  {
    id: "nairn-bar",
    name: "Nairn Bar",
    position: { lat: 57.6, lon: -3.85 },
    type: "buoy",
    rcs: 0.07,
    category: "north-cardinal",
  },
  {
    id: "lossiemouth-entrance",
    name: "Lossiemouth harbour entrance",
    position: { lat: 57.717, lon: -3.273 },
    type: "buoy",
    rcs: 0.07,
    category: "starboard-hand",
  },
  {
    id: "buckie-entrance",
    name: "Buckie harbour entrance",
    position: { lat: 57.682, lon: -2.96 },
    type: "buoy",
    rcs: 0.08,
    category: "port-hand",
  },
];

const LANES: ReadonlyMap<string, ReadonlyArray<LatLon>> = new Map([
  // Outer Moray Firth fairway running roughly E-W mid-firth — used by
  // through-traffic from the Sutors out to the Aberdeen offshore lanes.
  [
    "moray-firth-eastbound",
    [
      { lat: 57.7, lon: -4.05 },
      { lat: 57.7, lon: -3.7 },
      { lat: 57.71, lon: -3.4 },
      { lat: 57.72, lon: -3.1 },
      { lat: 57.73, lon: -2.85 },
    ] as ReadonlyArray<LatLon>,
  ],
  [
    "moray-firth-westbound",
    [
      { lat: 57.73, lon: -2.85 },
      { lat: 57.72, lon: -3.1 },
      { lat: 57.71, lon: -3.4 },
      { lat: 57.7, lon: -3.7 },
      { lat: 57.7, lon: -4.05 },
    ] as ReadonlyArray<LatLon>,
  ],
  // Inverness Firth approach — south-west into the Beauly Firth via
  // Chanonry, used by ships heading to Inverness Marina or the
  // Caledonian Canal sea-lock at Clachnaharry.
  [
    "inverness-firth-approach",
    [
      { lat: 57.7, lon: -4.0 },
      { lat: 57.65, lon: -4.05 },
      { lat: 57.59, lon: -4.09 },
      { lat: 57.55, lon: -4.15 },
      { lat: 57.5, lon: -4.22 },
    ] as ReadonlyArray<LatLon>,
  ],
  // Lossiemouth approach — northbound short fairway from offshore
  // into the harbour mouth.
  [
    "lossiemouth-approach",
    [
      { lat: 57.74, lon: -3.28 },
      { lat: 57.73, lon: -3.275 },
      { lat: 57.72, lon: -3.273 },
    ] as ReadonlyArray<LatLon>,
  ],
  // Buckie approach.
  [
    "buckie-approach",
    [
      { lat: 57.71, lon: -2.96 },
      { lat: 57.7, lon: -2.96 },
      { lat: 57.685, lon: -2.96 },
    ] as ReadonlyArray<LatLon>,
  ],
]);

const FERRY_TEMPLATES: ReadonlyArray<FerryTemplate> = [
  {
    id: "moray-firth-pilot-launch",
    name: "Inverness Firth pilot launch",
    vesselType: "pilot",
    rcs: 0.3,
    ais: {
      mmsi: "232100201",
      name: "INVERNESS PILOT",
      class: "A",
      destination: "STN.",
      callsign: "MMIP",
    },
    waypoints: [
      { atSimSeconds: 0, position: { lat: 57.6, lon: -4.0 }, cog: 240, sog: 12 },
      { atSimSeconds: 600, position: { lat: 57.56, lon: -4.12 }, cog: 240, sog: 14 },
      { atSimSeconds: 1500, position: { lat: 57.52, lon: -4.18 }, cog: 240, sog: 8 },
    ],
  },
  {
    id: "moray-firth-trawler-buckie",
    name: "Buckie trawler outbound",
    vesselType: "fishing",
    rcs: 0.45,
    state: "fishing",
    ais: {
      mmsi: "232100202",
      name: "STAR OF MORAY",
      class: "B",
      destination: "FISHING",
      callsign: "MABK",
    },
    waypoints: [
      { atSimSeconds: 0, position: { lat: 57.69, lon: -2.96 }, cog: 0, sog: 6 },
      { atSimSeconds: 900, position: { lat: 57.74, lon: -3.05 }, cog: 320, sog: 7 },
      { atSimSeconds: 1800, position: { lat: 57.78, lon: -3.2 }, cog: 290, sog: 5 },
    ],
  },
  {
    id: "moray-firth-cargo-eastbound",
    name: "Cromarty cargo (e-bound)",
    vesselType: "container",
    rcs: 0.85,
    ais: {
      mmsi: "232100203",
      name: "INVER FREIGHTER",
      class: "A",
      destination: "ABERDEEN",
      callsign: "MGAB",
    },
    waypoints: [
      { atSimSeconds: 0, position: { lat: 57.69, lon: -4.0 }, cog: 90, sog: 11 },
      { atSimSeconds: 900, position: { lat: 57.7, lon: -3.7 }, cog: 90, sog: 12 },
      { atSimSeconds: 1800, position: { lat: 57.71, lon: -3.4 }, cog: 95, sog: 12 },
      { atSimSeconds: 2700, position: { lat: 57.72, lon: -3.0 }, cog: 100, sog: 12 },
    ],
  },
];

export const MORAY_FIRTH: AreaData = {
  area: "moray-firth",
  boundingBox: {
    nw: { lat: 57.85, lon: -4.4 },
    se: { lat: 57.4, lon: -2.6 },
  },
  coastline: MORAY_FIRTH_COASTLINE,
  depthContours: [],
  marks: MARKS,
  lanes: LANES,
  raconIds: new Set(),
  ferryTemplates: FERRY_TEMPLATES,
};
